import store from '@/store'
import router from '@/router'

import * as MessageDict from 'utils/message'
import {
  updateTabUrl,
  getTabDetailUrl,
} from 'api/tab'
import {
  payOrderUrl
} from 'api/product'
import {
  MessageDialog as MessageDialogFactory
} from '@/plugins/createApi' // Pop up frame appears when calling   // 调用就出现弹框
import { createMessageDialog as createMessageDialogRaw } from './messageDialog'
import { stockShortageProducts } from './stockShortageInfo'
import {
  _filterOnlineData
} from '../filterOnlineData'
import dayjs from 'dayjs'
import { noteServerTime, venueTimeZoneOf } from '@/utils/venueClock'
import { openreplayHandlerError } from 'utils/openreplay'
import { t } from '@/utils/language'
import { enterVenue } from '@/store/venueEntry'
import { isMenuGoneResponse, menuGoneError } from '@/utils/menu/menuRead'
import { soldOutOfAddOrderFailure } from '@/utils/menu/soldOutEnvelope'
import { isMenuFallback, menuUpdatedDialogText, stillMarkedNames } from '@/utils/menu/menuChanged'
import { markSoldOutLines, rejectionIsCurrent, reviewAfterMenuUpdate } from '@/utils/menu/menuCheck'
import { withMenuMarksCarried } from '@/utils/menu/cartMarks'
import { currentPreviewMenuToken, PREVIEW_ORDERING_CODE } from '@/utils/menu/previewMenu'
import { previewRequestPlan, applyPreviewHeader, previewBlockedError, isPreviewBlockedError, previewTokenOfConfig } from '@/utils/menu/previewRequest'
import { notePreviewMenuAnswer } from '@/utils/menu/previewMenuStatus'
import { previewOrdersDisabledText } from '@/utils/menu/previewMenuCopy'

/**
 * 多版本菜单 B6（U-B6-2 review 第 2 轮 G2）：这一次 `success:false` 回答的处理里弹过提示没有。处理途中抛了错时（见响应拦截器
 * `_rejectAfterFailureHandlingThrew`），没弹过才补今天那句通用提示，弹过就不再补第二个。
 * 失败回答的处理是同步的（只有「菜单刚更新」那条是异步的，它自己兜住一切错误、不会在处理途中抛），进处理之前清零就够。
 * 这个文件里的弹窗都经过下面这两个包装 —— 与原来的弹窗工厂 / 提示一模一样（参数原样转过去），只多记一笔。
 */
let failureDialogShown = false
const MessageDialog = {
  $create(...args) {
    failureDialogShown = true
    return MessageDialogFactory.$create(...args)
  }
}
function _createMessageDialog(message) {
  failureDialogShown = true
  return createMessageDialogRaw(message)
}

const NOT_FOUND_VENUE_URL_PATH_LIST = [
  '/customer/default/get-venue'
]
// Tab Related API interface path
// tab 相关的 api 接口路径
const TAB_URL_LIST = [
  updateTabUrl,
  getTabDetailUrl,
]

const specialInteractList = [
  'customer/default/pay-order'
]
const ignoreErrorList = [
  'customer/default/get-user'
]
const notShowToastList = [
  '/customer/default/answer-campaign'
]

const restoreUserList = [
  '/customer/default/restore-user'
]

/** 合单页的路径：`/vgs/…`、外卖别名 `/online/vgs/…`（`/vgs/o/…` 也以 `/vgs/` 开头），见 `router/index.js` 的合单路由与别名 */
const SUB_GROUP_PATH = /^\/(online\/)?vgs\//

/**
 * 这一页是不是合单页（review 第 2 轮 G5：认别名）。先按路由记录认 —— vue-router 3 对别名匹配到的是原记录（`/vgs/:venueSubGroupId/…`），
 * 所以 `route.matched` 的 path 不管从哪个别名进来（今天的 `/online/vgs/…`、`/vgs/o/…`，以后再加的也一样）都以 `/vgs/` 开头；
 * 没有 matched 的路由对象（导航进行中拼出来的）再看 `route.path` 本身（认 `/online/vgs/` 别名）。
 */
function _isSubGroupRoute(route) {
  if (!route) return false
  const matched = Array.isArray(route.matched) ? route.matched : []
  if (matched.some(record => record && typeof record.path === 'string' && SUB_GROUP_PATH.test(record.path))) return true
  return typeof route.path === 'string' && SUB_GROUP_PATH.test(route.path)
}

/**
 * 多版本菜单 B6（U-B6-3）：现在是不是合单（owner D98①：合单成员店一律主菜单，读菜单不带预览头）。
 * 与 `utils/menu/qrMenu.js menuContextFromState` 同一个信号（`venueHome.subGroupId`，进合单成员店之后的商品页 / 购物车地址上没有 `?subGroupId`，
 * 只有它一路跟着；单店的预览入口会把上一次合单留下的值清掉，`venue-home _setupSubGroup`，review 第 2 轮 G5），另加合单页本身（路由或地址栏，认别名）。
 */
function _isSubGroupContext(route) {
  if (store.state.venueHome && store.state.venueHome.subGroupId) return true
  const addressPath = typeof window !== 'undefined' && window.location ? window.location.pathname : ''
  return _isSubGroupRoute(route) || SUB_GROUP_PATH.test(addressPath)
}

/** 这个请求自己说了不要失败提示（`silentErrors: true`，后台读；review 第 2 轮 G7） */
function _isSilentRequest(config) {
  return !!config && config.silentErrors === true
}

/**
 * 这个回答是哪个接口的（review 第 3 轮 H1）：真请求回来的看 `response.request.responseURL`；走缓存回来的（`cacheRequest.js` 的
 * axios-cache-interceptor 0.10.2：get-product / get-venue / get-user，5 秒内的重复与同时发出的同一个请求合并成一个）是拼出来的响应，
 * **没有 `request`** —— 用请求配置的 `url`，这样它与真请求走同一条分支。都没有 ⇒ ''（按接口分的分支一个都不进，照通用处理）。
 */
function _responseUrlOf(response) {
  const request = response && response.request
  if (request && typeof request.responseURL === 'string' && request.responseURL) return request.responseURL
  const config = response && response.config
  return config && typeof config.url === 'string' ? config.url : ''
}

/** 下单 / 付款接口按码拒的回答（`error.info` 里带 `##<n>##`，交给 `_addOrderErrorDialog` 的那种） */
function _isAddOrderCodeAnswer(responseURL, error) {
  const isOrderUrl = responseURL.includes('/customer/default/pay-order') || responseURL.includes('/customer/default/add-order')
  return isOrderUrl && !!error && typeof error.info === 'string' && error.info.includes('##')
}

// A/B test framework: only customer-facing API paths receive seed/force-variant headers.
// A/B 测试框架：只有顾客侧接口路径会带上 seed / force-variant header。
function _isCustomerFacingApi(url) {
  return /\/(customer|menu|order|recommend)/.test(url)
}

const handleErrorPage = errorUrl => {
  if (NOT_FOUND_VENUE_URL_PATH_LIST.includes(errorUrl)) {
    router.push('/not-find-venue')
    store.commit('closeLoading')
  }
}

const jumpToUpdatePage = function () {
  const {
    tabId,
    venueId
  } = router.currentRoute.params
  router.push(`/v/${venueId}/tab-admin/${tabId}/update`)
}

const jumpToMenu = function () {
  const {
    tabId,
    venueId
  } = router.currentRoute.params
  router.push(`/v/${venueId}/tab-admin/${tabId}/menu`)
}

const jumpToCart = function (query = '') {
  const {
    venueId
  } = router.currentRoute.params
  const {
    table
  } = store.state?.details
  // If the current page is the shopping cart page, you do not need to jump again
  // 若当前就是购物车页，则不需要再跳转
  // if (router.currentRoute.name === 'cart-pick') return
  router.push(`/v/${venueId}/cart/${table ? `t/${table}` : 'p'}${query}`)
}

/**
 * 多版本菜单 B6（U-B6-2，owner D96 / D2 = A）：下单被拒、后端说这一单的菜单已经不适用（售罄信封 `menuFallback: true`）⇒
 * 照搬 kiosk 的「菜单刚更新」弹窗代替通用提示（文案见 utils/menu/menuChanged.js menuUpdatedDialogText）。
 * 被拒的行在这之前已经按 id 标好了（来历 fallback）。**先**开着加载圈按这一单的用餐方式重新解析一次菜单、读一次新菜单的商品表、
 * 撤掉新菜单上能下单的回落标记、D97 重判（utils/menu/menuCheck.js reviewAfterMenuUpdate），**再**弹：名单是核对之后仍标着的这次被拒的行
 * （购物车上的名字），一行都不剩就只有标题；重新解析 / 读表失败照今天用后端给的名字。
 * 按钮 Review order 只回购物车；顾客自己删标出来的行，**不自动重新下单**。
 * 弹窗不用 TabMessageDialog 那个共享单例（`$create(config, false)`，vue-create-api 0.2.4 `create(config, renderFn, _single)`：
 * 第二个参数不是函数就当 `_single`）—— 不然之后任何一个 `_createMessageDialog`（后台请求的「Network error」等）都会把它的文字和唯一的按钮换掉。
 * 永不 reject（调用方拿它排在拒单之前）。
 * @param {Object} soldOut 归一后的售罄信封（utils/menu/soldOutEnvelope.js）
 * @param {String|Object} requestData 被拒那一单的请求体
 * @param {String[]} fallbackIds 这次拒单新加上的回落标记（markSoldOutLines 的返回值）
 */
async function _menuUpdatedDialog(soldOut, requestData, fallbackIds) {
  store.commit('showLoading')
  let review = null
  try {
    review = await reviewAfterMenuUpdate(store, requestData, { fallbackIds })
  } catch (error) {
    console.log(error)
  }
  store.commit('closeLoading')
  const stillMarked = review && review.reviewed ? stillMarkedNames(store.state, soldOut.ids) : undefined
  try {
    const dialog = MessageDialog.$create({
      $props: {
        ...MessageDict.delinery,
        ...menuUpdatedDialogText(soldOut, stillMarked)
      },
      $events: {
        click() {
          // review 第 2 轮 G8：不是共享单例（上面那条），点完要自己拆掉 —— TabMessageDialog 点按钮只是藏起来，
          // 不 remove 的话每弹一次就在 body 下留一个藏着的实例（vue-create-api 0.2.4 的 remove = 销毁实例 + 拿掉 DOM）
          if (dialog && typeof dialog.remove === 'function') dialog.remove()
          jumpToCart()
        }
      }
    }, false)
    dialog.show()
  } catch (error) {
    console.log(error)
  }
}

/**
 * 多版本菜单 B6（U-B6-2 review 第 2 轮 G2）：处理一个 `success:false` 回答的途中抛了错 ⇒ 仍照后端拒单收尾 ——
 * 记日志；这次处理还没弹过提示就补一个；关加载圈；
 * reject 后端的错误对象（调用方据此判「明确没下成」，`utils/orderPlaced.js`），不是那个 Error（那会被当成「不知道」）。
 * 补哪个提示（review 第 3 轮 H4）：下单 / 付款按码拒的回答（`##<n>##`）⇒ 这个码本来那句顾客文案（`_addOrderFallbackDialog`）——
 * 后端那句 message 在这里是给开发看的 `AddOrder validate error.`；别的回答 ⇒ 后端给的那句 message（与下面通用分支同一句）。
 * 补提示自己再抛也只记日志，照样关加载圈、照后端拒单 reject。
 * @param {*} thrown 处理途中抛出来的东西
 * @param {*} error 后端回答里的 error
 * @param {String} responseURL 这个回答是哪个接口的（`_responseUrlOf`）
 */
function _rejectAfterFailureHandlingThrew(thrown, error, responseURL) {
  console.log(thrown)
  if (!failureDialogShown) {
    try {
      if (_isAddOrderCodeAnswer(responseURL, error)) {
        _addOrderFallbackDialog(error.info)
      } else {
        const message = error && typeof error === 'object' ? error.message : error
        if (typeof message === 'string' && message) _createMessageDialog(message)
      }
    } catch (dialogThrown) {
      console.log(dialogThrown)
    }
  }
  store.commit('closeLoading')
  return Promise.reject(error)
}

/**
 * `_addOrderErrorDialog` 各码自己那句里**不靠 `info[2]`** 的（标题 / 正文）。弹窗与处理途中抛错的兜底（review 第 3 轮 H4）共用这一份字。
 * 1 / 2 走翻译（按此刻的语言取，所以是函数），别的照今天写死英文；11 的正文是按缺库存名单拼的，这里只有标题。
 * 不在这里的码：3 / 4（回购物车验会员码，没有弹窗）、6 / 7（句子里要 info[2] 里的名字 / 金额）、12 与不认识的（今天是空弹窗）。
 */
const ADD_ORDER_DIALOG_COPY = {
  0: () => ({ title: 'Items unavailable', content: 'Some of the items in your cart are unavailable. Please remove these from your cart before continuing.' }),
  1: () => ({ title: t('pickup_time_unavailable', 'Pickup time unavailable'), content: t('change_pickup_time', 'Please change your scheduled pickup time before continuing.') }),
  2: () => ({ title: t('delivery_time_unavailable', 'Delivery time unavailable'), content: t('change_delivery_time', 'Please change your scheduled delivery time before continuing.') }),
  5: () => ({ title: 'Included breakfast limit', content: 'Your included breakfast limit has been exceeded. We are unable to process your order.' }),
  8: () => ({ title: 'The delivery fee is changed', content: 'We have changed the amount of your order. Please place the order again.' }),
  9: () => ({ title: 'Something changed', content: 'Some pricing has changed while you were ordering. Your cart has been updated.' }),
  10: () => ({ title: 'Your scheduled time is no longer available.', content: 'Please select a different pickup time to place your order.' }),
  11: () => ({ title: 'The selected item is out of stock', content: '' })
}

/** 这个码不靠 info[2] 的那句（`ADD_ORDER_DIALOG_COPY`）；没有 ⇒ null */
function _addOrderFixedCopy(code) {
  return Object.prototype.hasOwnProperty.call(ADD_ORDER_DIALOG_COPY, code) ? ADD_ORDER_DIALOG_COPY[code]() : null
}

/** `##<n>##` 弹窗点了之后去哪（`_addOrderErrorDialog` 原来按钮里那三行，兜底弹窗共用） */
function _addOrderErrorJump(code) {
  if (code === 10) jumpToCart('?openDeliveryInfo=true&cleanLaterTime=true')
  else if (code >= 5 || !code) jumpToCart()
  else if (router.currentRoute.name !== 'cart-pick') jumpToCart('?openDeliveryInfo=true')
}

/**
 * review 第 3 轮 H4：处理下单 / 付款的 `##<n>##` 回答途中抛了错（`##11##` 名单读不出、`##0##` / `##6##` / `##7##` 缺 info[2]）⇒
 * 弹这个码本来那句（`ADD_ORDER_DIALOG_COPY`，与它的弹窗同一份字）；这个码没有不靠 info[2] 的那句 ⇒ `##0##` 那句（「Items unavailable」）。
 * 点了照那个码的弹窗走（用 `##0##` 那句时照 `##0##` 回购物车）。不标红（名单读不出 / 缺 info[2]，没有可标的 id）。
 * @param {String} info 后端回答的 error.info
 */
function _addOrderFallbackDialog(info) {
  const parsed = Number(String(info).split('##')[1])
  const fixed = _addOrderFixedCopy(parsed)
  const code = fixed ? parsed : 0
  MessageDialog.$create({
    $props: {
      ...MessageDict.delinery,
      ...(fixed || _addOrderFixedCopy(0))
    },
    $events: {
      click() {
        _addOrderErrorJump(code)
      }
    }
  }).show()
}

/**
 * Display the error pop-up when adding an order
 * @param {Object} response API return data
 */
/**
 * 显示添加订单时的报错弹窗
 * @param {Object} response API 返回数据
 */
function _addOrderErrorDialog(response) {
  const info = response.data.error.info.split('##')
  const code = Number(info[1])
  const message = MessageDict.delinery
  // 这个码不靠 info[2] 的那句（与处理途中抛错的兜底共用一份字，review 第 3 轮 H4）；没有的码（3 / 4 / 6 / 7 / 12 / 不认识的）先是空的
  let { title, content } = _addOrderFixedCopy(code) || { title: '', content: '' }
  let expireProductList = []
  /**
   * Time and commodity verification
   * 0 Items
   * 1 Pickup
   * 2 Delivery
   * 3 (no longer sent — see the note below `case 3:`)
   * 4 member
   * 5 guests
   * 6 combo
   * 7 Takeout system, delivery type, change verification of delivery fee
   * 8 Takeout system, delivery type, distribution fee change verification
   * 9 Change in total order price
   * 10 Time is not optional due to order throttling
   * 11 out of stock Insufficient commodity inventory
   */
  /**
   * 时间和商品校验
   * 0 Items
   * 1 Pickup
   * 2 Delivery
   * 3 后端已经不再发这个码（见下面 `case 3:` 旁边那段说明）
   * 4 member
   * 5 guests
   * 6 combo
   * 7 外卖系统，delivery 类型，起送费变动校验
   * 8 外卖系统，delivery 类型，配送费变动校验
   * 9 订单总价发生变动
   * 10 order throttling 导致时间不可选
   * 11 out of stock 商品库存不足
   */
  // 1 / 2 / 5 / 8 / 9 / 10 / 11 的标题 / 正文上面已经从 ADD_ORDER_DIALOG_COPY 拿了；下面只剩各码另外要做的事
  switch (code) {
    case 0:
      expireProductList = info[2].split(',')
      break

    // `case 3:` 现在是**死分支**，留着只是因为删它没有任何好处。
    //
    // 2026-08-10 那次删 member_2for1 / kid_free 时，这里的注释从「kid_free」改成了
    // 「venue_sp_code 规则」，理由是「后端 ##3## 校验的是 getSpRules 返回的全部特惠规则」
    // —— **那句是错的**。后端复查：`##3##` 全仓只有 `VenueDiscountDb::checkKidFreeDiscount()`
    // 一个发出方（三处 return），而那个方法已经随 kid_free 一起删掉了，
    // 现在后端能发出的码是 0/1/2/4/5/6/7/8/9/10/11/12，没有 3。
    //
    // 保留的理由只有一条：它与 `case 4`（member）共用同一段跳转，删掉是零收益的改动；
    // 万一哪天后端把 3 号码再用起来，落进 `case 4` 那段总比落进 default 的空白弹窗好。
    case 3:
    case 4:
      jumpToCart('?verifyCouponCode=true')
      return

    case 6:
      title = ''
      content = `Some products or options for ${info[2].split(',')} are not available now, please check and select again.`
      break

    case 7:
      title = ''
      content = `The starting delivery price of takeout has changed to $${info[2].split(',')}.`
      break
  }

  // Product error, type=0
  // 商品出错，type = 0
  if (expireProductList.length) expireProductList = expireProductList.map(item => ({ id: item, type: 0 }))

  if (code === 11) { // out of stock. When commodity inventory is insufficient   // out of stock 商品库存不足时
    // Get the information list of goods in short stock
    // 获取缺库存的商品信息列表。多版本菜单 B6（U-B6-2 review 第 2 轮 G2）：以前「所有反斜杠一删再 JSON.parse」，商品名带 `"` 就抛 SyntaxError，
    // 后端明确的拒单被调用方当成「不知道下没下成」；现在按后端的转义层数还原（stockShortageInfo.js）。还是读不出就抛 ——
    // 响应拦截器的兜底照后端拒单处理（弹缺库存那个标题 —— review 第 3 轮 H4、reject 后端的错误对象），标记一条不动
    const productList = stockShortageProducts(info[2])
    if (!productList) throw new Error('##11## 缺库存名单读不出')
    // ID list of goods in short stock
    // 缺库存的商品 id 列表
    const productIdList = productList.map(item => item.venueProductId)
    // Name list of goods in short stock
    // 缺库存的商品 name 列表
    const productNameList = productList.map(item => item.name)
    // When jumping back to the shopping cart, the items with these IDs will be displayed in the deletion status
    // 跳回购物车时，将这些 id 的商品，展示成删除状态
    // Commodity is out of stock, type=1
    // 商品缺库存，type = 1
    expireProductList = productIdList.map(item => ({ id: item, type: 1 }))
    // Pop up window of goods out of stock, display copy
    // 缺库存的商品弹窗，展示文案
    productNameList.forEach((item, index) => {
      if (index) content += ' \n '
      content += `${item} is out of stock.`
    })
  }

  // 多版本菜单 B6（U-B6-2 review 第 1 轮 F2）：整份替换照旧，但带上本地核对标的「不在当前菜单上」那几行（source: menu），
  // 不然后端的一次别的回答会把它们悄悄放行；没有这种标记时与以前逐字节相同
  store.commit('saveExpireProductList', withMenuMarksCarried(store.state.expireProductList, expireProductList))
  MessageDialog.$create({
    $props: {
      ...message,
      title,
      content,
    },
    $events: {
      // ISMobile represents whether it is a mobile terminal. It needs a jump route, so it needs to be differentiated
      // isMobile 代表是不是手机端,这里需要跳转路由所以要做区分
      click(isMobile) {
        _addOrderErrorJump(code)
      }
    }
  }).show()
  store.commit('closeLoading')
}

export default function (service, baseURL) {
  service.headers = {
    'Content-Type': 'application/json'
  }

  // request interceptor
  // request interceptor 请求拦截
  service.interceptors.request.use(
    config => {
      if (store.getters.token && config.baseURL === baseURL) {
        config.headers.Authorization = `Bearer ${store.getters.token}`
      }

      if (config.method !== 'get' && !config.data) {
        config.dailsta = {}
      }

      // 记下发出时间：响应里的 serverTime 只在往返够快时才拿来校门店时钟（utils/venueClock.js，review 第 1 轮 #3）
      config.metadata = { ...(config.metadata || {}), requestStartedAt: Date.now() }

      // A/B test framework: attach X-Order-Seed and X-Force-Variant on customer-facing
      // API calls only (menu / order / customer / recommend). Internal admin/debug
      // endpoints are excluded so they do not pollute experiment buckets.
      // A/B 测试框架：仅给顾客侧接口（menu / order / customer / recommend）带上
      // X-Order-Seed 和 X-Force-Variant，避免内部管理接口污染实验分桶。
      if (_isCustomerFacingApi(config.url || '')) {
        const seed = store.state.orderSeed
        if (seed) {
          config.headers['X-Order-Seed'] = seed
        }
        try {
          const force = sessionStorage.getItem('mm_ab_force')
          if (force) {
            config.headers['X-Force-Variant'] = force
          }
        } catch (e) {
          // sessionStorage may be unavailable; ignore.
          // sessionStorage 可能不可用，忽略即可。
        }
      }

      // 多版本菜单 B6（U-B6-3，owner D80 / D98②）：地址里带着形状合法的预览码 ⇒ /customer/ 下的请求带 X-Preview-Menu、绕开缓存；
      // 后端没有预览闸的写接口（付款 / 挂账 / 合单下单）在这里就拦下、不发出去。码每次从当前地址现读，**不存任何地方**
      // （见 utils/menu/previewMenu.js、previewRequest.js）
      const route = router.currentRoute || {}
      const previewToken = currentPreviewMenuToken(route.query)
      const previewPlan = previewRequestPlan({ url: config.url, token: previewToken, subGroup: !!previewToken && _isSubGroupContext(route) })
      if (previewPlan.block) throw previewBlockedError(config.url)
      if (previewPlan.header) applyPreviewHeader(config, previewToken)

      return config
    },
    error => {
      return Promise.reject(error)
    }
  )

  // response interceptor. Return interception / error interception
  // response interceptor 返回拦截 / 错误拦截
  service.interceptors.response.use(
    response => {
      // Judge whether it is the download pdf interface
      // 判断是否是下载 pdf 接口
      if (!response.data.data && response.data.type === 'application/pdf') {
        return response.data
      }
      const {
        success,
        error,
        data,
        serverTime,
        serverTimeZone
      } = response.data

      // Current restaurant time
      // 当前餐厅时间
      let currentVenueTime = dayjs.tz().format('YYYY-MM-DD HH:mm:ss')
      // If serverTime exists, assign the serverTime to the current restaurant time after time zone conversion, otherwise use current venue time as input
      // 若存在 serverTime，则将 serverTime 进行时区转换后，赋值给当前餐厅时间, otherwise use current venue time as input
      if (data?.currentVenueTime) {
        currentVenueTime = data.currentVenueTime
      } else {
        if (serverTime) {
          // Convert serverTime to serverTimeZone of the restaurant
          // 将 serverTime 转换成餐厅 serverTimeZone 的时间
          const time = dayjs(serverTime).tz(serverTimeZone, true).valueOf()
          currentVenueTime = dayjs(time).tz().format('YYYY-MM-DD HH:mm:ss')
        }
      }
      store.commit('saveCurrentVenueTime', currentVenueTime)
      // 附加费按服务器时钟判定（后端就是这么判的），记下服务器与手机的时间差。
      // 走缓存回来的响应（5 秒内的旧 serverTime）不算，免得把偏移拉偏。
      // 往返太久（标签页被冻结 / 网络卡住，回调晚跑）的样本 noteServerTime 自己会丢掉（review 第 1 轮 #3）。
      if (serverTime && !response.cached) noteServerTime(serverTime, serverTimeZone, Date.now(), response.config?.metadata?.requestStartedAt)

      // error.message
      if (!success) {
        // 多版本菜单 B6（U-B6-2 review 第 2 轮 G2）：后端明确的拒单永远不会变成「不知道下没下成」——
        // 下面处理这个回答的途中不管哪里抛了错（`##11##` 名单读不出、`##0##` 缺 info[2] …），都照后端拒单收尾
        failureDialogShown = false
        // review 第 3 轮 H1：按接口分的判断一律用这一个地址 —— 走缓存回来的（get-product / get-venue / get-user）没有 response.request，
        // 以前读 response.request.responseURL 时抛错、落进下面 catch 的兜底（本来不弹的 get-user 也弹了后端那句、get-venue 不跳「找不到门店」页）
        const responseURL = _responseUrlOf(response)
        try {
          // Separately block the official website - contact page submission interface, and return an error message
          // 单独拦截官网 - contact 页提交接口，并返回错误信息
          if (responseURL.includes('default/contact')) {
            return Promise.reject(error.info)
          }

          // 多版本菜单 B6：带了 menuId 的读菜单请求，后端说这份菜单已不在用 ⇒ 不弹窗（那句是给开发看的），
          // 交给调用方清掉 menuId、静默重新解析一次（utils/menu/menuRead.js readWithMenuRetry）
          if (isMenuGoneResponse(response.config.url, error, response.config.data)) {
            return Promise.reject(menuGoneError())
          }

          // 多版本菜单 B6（U-B6-2 review 第 2 轮 G7）：请求自己说了不要提示（后台读，顾客什么都没点，`api/product.js getProductListQuietly`）⇒
          // 一个提示都不弹，把后端的错误原样交给调用方。没带这个开关的请求一切照今天
          if (_isSilentRequest(response.config)) {
            return Promise.reject(error)
          }

          // 多版本菜单 B6（U-B6-3）：页面本地拦单漏了、请求带着 X-Preview-Menu 到了后端，后端拒（D80）⇒ 显示后端那句
          // （"This is a menu preview, so orders can't be placed."，后端已按语言翻好）；缺了就用 D56 那句。不走别的分支
          if (error && error.code === PREVIEW_ORDERING_CODE) {
            _createMessageDialog(error.message || previewOrdersDisabledText())
            store.commit('closeLoading')
            return Promise.reject(error)
          }

          // 多版本菜单 B6：单店下单的售罄信封有两种形状（名字数组 / 带 menuId 之后的对象），归一后挂在错误上给调用方用
          const soldOut = soldOutOfAddOrderFailure(response.config.url, error, response.config.data)
          if (soldOut) error.soldOut = soldOut
          // U-B6-2：带 id 的信封（对象形状）⇒ 按 id 把购物车里那几行标出来（老站现成的过期标红，合并、不清车）；
          // 名字数组（老后端 / 没带 menuId）一行不标，提示也照今天（下面的通用提示）。
          // review 第 1 轮 F3：只认 store 此刻的门店 + 菜单发出去的那一单 —— 迟到的拒单（顾客已经换了店 / 菜单）不标、不走「菜单刚更新」，照今天的通用提示
          if (soldOut && rejectionIsCurrent(store.state, response.config.data)) {
            const addedIds = markSoldOutLines(store, soldOut)
            // owner D96 / D2 = A：后端说这一单的菜单已经不适用（menuFallback: true）⇒ 重新解析、核对之后弹「菜单刚更新」代替通用提示；
            // 拒单在弹窗之后才交给调用方（调用方的 .catch 会关加载圈，这期间要一直开着）
            if (isMenuFallback(soldOut)) {
              const rejectAfterDialog = () => Promise.reject(error)
              return _menuUpdatedDialog(soldOut, response.config.data, addedIds).then(rejectAfterDialog, rejectAfterDialog)
            }
          }

          const isShowErrorDialog = _isAddOrderCodeAnswer(responseURL, error)
          if (isShowErrorDialog) {
            _addOrderErrorDialog(response)
            return Promise.reject(error)
          }

          // If it is a tab related interface, block it separately
          // 如果是 tab 相关的接口，单独拦截
          const isNoTab = responseURL.includes(payOrderUrl) && (error.message === 'no_tab' || error.message === 'close_tab')
          if (isNoTab || TAB_URL_LIST.find(item => responseURL.includes(item))) {
            const code = error.message
            const message = MessageDict[code]
            if (message && !message.custom) {
              MessageDialog.$create({
                $props: message,
              }).show()
            }
            return Promise.reject(code)
          }

          // If the type is form, then each name in this object is the name of the form element, and the value is the error message
          if (specialInteractList.find(item => responseURL.includes(item))) {
            if (router.currentRoute.name === 'start-tab') {
              let code
              if (error.message === 'tab_insufficient_fund') {
                code = error.message
              } else {
                code = 'othersStartTabError'
              }
              const message = MessageDict[code]
              if (message && !message.custom) {
                MessageDialog.$create({
                  $props: message,
                }).show()
              } else {
                _createMessageDialog(error.message || error)
              }
            } else {
              // If it is tab_ not_ Start, you need to return tabOpenTime, which is in the tabOpenTime field of error
              // 如果是 tab_not_start，需要返回 tabOpenTime，这个字段在 error 的 tabOpenTime field 中
              if (error.message === 'tab_not_start') {
                return Promise.reject(error)
              } else if (error.message === 'tab_insufficient_fund') {
                const code = 'tab_insufficient_fund_increase_tab'
                const message = MessageDict[code]
                if (message && !message.custom) {
                  MessageDialog.$create({
                    $props: message,
                  }).show()
                } else {
                  _createMessageDialog(error.message || error)
                }
              } else if (error?.message.indexOf('tab') < 0) {
                const code = error.message
                if (MessageDict[code] && !MessageDict[code].custom) {
                  MessageDialog.$create({
                    $props: MessageDict[code],
                  }).show()
                } else {
                  _createMessageDialog(error.message || error)
                }
              }
            }
            return Promise.reject(error.message || error)
          }
          if (error && ignoreErrorList.find(item => responseURL.includes(item))) {
            return Promise.reject(error.message || error)
          }

          // 3. Normal interception and jump
          // 3. 正常拦截，跳转
          // Toast is not displayed when the API is answer campaign
          // API 为 answer-campaign 时，不显示 Toast
          const isShowToast = notShowToastList.find(item => responseURL.includes(item))
          const isRestoreUser = restoreUserList.find(item => responseURL.includes(item))
          if (!isShowToast) {
            if (error && error.type === 'form') {
              if (error.info.length) {
                _createMessageDialog(error.message)
              } else {
                _createMessageDialog(error.message)
              }
              handleErrorPage(response.config.url)
              return Promise.reject(error.info.length ? error.info : error.message)
            } else if (error) {
              _createMessageDialog(error.message)
            }
          }
          return Promise.reject(error)
        } catch (thrown) {
          return _rejectAfterFailureHandlingThrew(thrown, error, responseURL)
        }
      } else {
        // 多版本菜单 B6（U-B6-3，D88）：带着预览码发出去的请求回来了 resolvedMenu ⇒ 判这个码还在预览还是已结束（横幅换那句话）
        const sentPreviewToken = previewTokenOfConfig(response.config)
        if (sentPreviewToken && data && data.resolvedMenu && typeof data.resolvedMenu === 'object') {
          notePreviewMenuAnswer(sentPreviewToken, data.resolvedMenu)
        }

        /**
         * Demand:
         * When the current customer accesses the tab, if the payment status of the tab is pending error, the popup of efficient fund will be displayed until the user closes. After closing, the user will automatically enter the modify information page and can modify the credit card information
         * If successful, judge/get tab detail and the payment status of the interface is equal to 6 (isPendingErrorPaymentStatus)
         */
        /**
         * 需求：
         * 当前端 customer 访问 tab 的时候，tab 的 payment status 是 pending error 的话，显示 insufficient fund 那个 popup，直到用户关闭，关闭后自动进入修改信息页面并且可修改信用卡信息
         * 成功则判断 /get-tab-detail 并且 接口的payment status 等于 6 (isPendingErrorPaymentStatus)
         */
        const tabDetailUrl = '/customer/default/get-tab-detail'
        const isTabDetailAPI = response.config.url === tabDetailUrl
        const isPendingErrorPaymentStatus = response.data.data.paymentStatus === 6
        const isTabOpening = response.data.data.paymentStatus === 3
        if (isTabDetailAPI) {
          if (isPendingErrorPaymentStatus) {
            const message = MessageDict.tab_insufficient_fund_payment
            MessageDialog.$create({
              $props: message,
              $events: {
                click() {
                  jumpToUpdatePage()
                }
              }
            }).show()
          } else if (isTabOpening) {
            /**
             * Demand:
             * This page cannot be modified when the tab has been opened, or when the paymentStatus is pending, that is, 3.
             * However, when I manually refresh this page, I can still modify it regardless of the tab status, although an error will be reported. But I hope that if the tab status does not correspond, it will automatically return to the main page
             */
            /**
             * 需求：
             * 这个页面是当 tab 已经 open 了的话是不能修改的，或者 paymentStatus 是 pending 的话也就是 3 的话，是不能修改的。
             * 但是当我手动刷新这个页面，是无视 tab 状态依旧可以修改的，虽然会报错。但是希望如果 tab 状态不对应的话，应该自动返回主页面
             */
            if (router.currentRoute.name === 'tab-update') {
              const message = MessageDict.tab_is_open
              MessageDialog.$create({
                $props: message,
                $events: {
                  click() {
                    jumpToMenu()
                  }
                }
              }).show()
            }
          }
        }
      }

      // TODO Reconfigurable, saving the whole data of get venue in vuex, rather than saving them one by one
      // TODO 可重构，将 get-venue 的整体数据存到 vuex 中，而不是逐个保存
      if (response.config.url === '/customer/default/get-venue') {
        // If the current restaurant ID is inconsistent with the restaurant ID in vuex, initialize the vuex data
        // 若当前餐厅 id 和 vuex 中的餐厅 id 不一致，则初始化 vuex 数据（resetState / resetAppState / resetCartState /
        // resetContactDetailsState / resetVenueHomeState），然后**紧接着**把这一单的 seed 绑到这家店的 UUID ——
        // 以前 resetState 会清掉 App.vue 刚生成的 seed，首页的 get-menu 发出去没有 seed（多版本菜单 B6，store/venueEntry.js）
        if (enterVenue(store, data)) {
          if (data.currentVenueTime) {
            store.commit('saveCurrentVenueTime', data.currentVenueTime)
          }
        }

        store.commit('saveVenueUrlId', data.url_id)
        store.commit('saveVenueCountry', data.country || 'AU')
        store.commit('saveCurrency', data.currency || 'AUD')
        store.commit('saveVenueAddress', data.address)
        if (data.venueGroupId) {
          store.commit('saveVenueGroupId', data.venueGroupId)
        }
        store.commit('saveCreditCardSurchargeLabel', data.surchargeLabel || 'Venue Surcharge')

        const enableWaitingTime = data.settings?.waitingTimeSettings?.enableWaitingTime ?? 600
        let averageWaitingTime = 600
        // If the restaurant has not set WaitingTime, the default delivery setting is 600
        // 若餐厅没有设置过 WaitingTime，那么 delivery 默认设置 600
        if (typeof enableWaitingTime === 'number') averageWaitingTime = enableWaitingTime
        else averageWaitingTime = (enableWaitingTime && data.averageWaitingTime) ? data.averageWaitingTime : data.settings?.waitingTimeSettings?.defaultWaitingTime

        store.commit('saveVenueAverageWaitingTime', averageWaitingTime)
        store.commit('saveVenueDeliverySetting', data.settings.deliverySettings ?? {})
        if (data.settings.waitingTimeSettings) {
          store.commit('saveVenueSystemDefaultWaitingTime', data.settings.waitingTimeSettings.systemDefaultWaitingTime)
          store.commit('saveVenuePeakWaitingTimes', data.settings.waitingTimeSettings.peakWaitingTimes)
        }
        // 门店时区与附加费判定用的是同一处解析（utils/venueClock.js venueTimeZoneOf，review 第 1 轮 #11）；
        // 没配时区时按后端默认的墨尔本（以前留着上一家店的时区）
        store.commit('saveVenueTimeZone', venueTimeZoneOf(data))
        if (data.settings?.options) store.commit('saveVenueIsGuests', data.settings?.options?.enableGuest && data.settings?.options?.spTableRule)
        store.commit('saveVenueSpDiscount', data.settings.options.spDiscount ? data.settings.options.spDiscount : {
          enable: false
        })
        if (data.settings.weeklyOperatingHours) store.commit('saveVenueWeeklyOperatingHours', data.settings.weeklyOperatingHours)
        if (data.email) store.commit('saveEmail', data.email)
        if (data.phone) store.commit('savePhone', data.phone)
        if (data.settings.discountRules) store.commit('saveVenueDiscountRules', data.settings.discountRules)
        if (data.settings.preOrderSettings) store.commit('saveVenuePreOrderSettings', data.settings.preOrderSettings)
        if (data.settings.pickupSettings) store.commit('saveVenuePickupSettings', data.settings.pickupSettings)
        store.commit('saveVenuePostWidgetSettings', data.settings.postWidgetSettings ?? {})
        store.commit('saveVenueStationSettings', data.settings?.stationSettings ?? {})
        store.commit('saveVenueCustomMessageSettings', data.settings?.customMessageSettings ?? {})
        store.commit('saveVenueEnablePaymentGateway', data.settings?.enablePaymentGateway ?? {})
        store.commit('saveVenueGroupSettings', data.settings?.venueGroup ?? {})
        store.commit('saveModificationDescSettings', data.settings?.modificationDescSettings ?? {})

        // spSettings - cartAddProduct
        if (data.settings?.spSettings?.cartAddProduct?.enable) {
          let cartAddProductList = data.settings?.spSettings?.cartAddProduct?.products ?? []
          if (cartAddProductList.length) {
            cartAddProductList = cartAddProductList.map(item => ({
              ...item.detail,
              id: item.id,
              num: item.no,
              venueId: store.state.venueId,
              disableOperation: true,
            }))
          }
          store.commit('saveCartAddProduct', cartAddProductList)
        } else {
          store.commit('saveCartAddProduct', [])
        }
      }

      // After successful payment, clear the member discount
      // 支付成功后，清空会员优惠
      if (response.config.url === '/customer/default/pay-order') {
        store.commit('clearDiscounts')
      }
      return store.state.app.isOnline ? _filterOnlineData(data, response.config.url) : data
    },

    error => {
      // 多版本菜单 B6（U-B6-3）：预览时本地拦下、根本没发出去的写接口（请求拦截器抛的）⇒ 说 D56 那句，不当成网络错误
      if (isPreviewBlockedError(error)) {
        _createMessageDialog(previewOrdersDisabledText())
        store.commit('closeLoading')
        return Promise.reject(error)
      }

      if (!error.response) {
        console.log(error.config ?? [])
        if (error.request) console.log(error.request)
        if (error.message) console.log(error.message)
        // 后台读（silentErrors，review 第 2 轮 G7）不弹；别的请求照今天
        if (!_isSilentRequest(error.config)) _createMessageDialog('Network error, please refresh your page and try again')

        return Promise.reject()
      }
      // const data = error.response.data || {}

      // When interface 500 is wrong, submit the error to openreplay
      // 接口 500 错误时，向 openreplay 提交错误
      if (error.response.status === 500) {
        openreplayHandlerError(error)
      }

      if (error.response.status === 401) {
        store.dispatch('user/logout')
      }
      return Promise.reject(error)
    }
  )
}
